// The provider modal (SCR-575): one provider's entry in the org's global allow-list. "Follow AWS"
// allows every model AWS makes invokable; "Choose models" allows exactly the ticked ones, each
// named for a person with its family name in mono beneath. The footer says, per agent, whether its
// default survives the change, with a re-point select for each one it would disable; a refusal of
// the save (`default_would_be_disabled`) lands there too. Built on the library's Dialog (focus
// trap, Esc, labelled by its title).
import { useId, useState } from "preact/hooks";
import type { ApiError } from "../../api/errors";
import { useReadOnly } from "../../host/readonly";
import { Dialog } from "../../primitives/dialog";
import { Field } from "../../primitives/field";
import type { HelpId } from "../../primitives/help";
import { Chip, Segmented } from "../../primitives/page";
import { ErrorBanner } from "../../primitives/states";
import { useUnsavedGuard } from "../../primitives/subtabs";
import type { Json } from "../../settings/inherit";
import { providerLabel } from "../../settings/pickers";
import { agentErrorsFrom } from "./agents-model";
import type { Harness } from "./model";
import { applyProviderEdit, defaultsFooter, entryFor, globalOf, type CatalogModel, type ProviderEdit, type ProviderEditResult, type ProviderPolicy } from "./provider-model";

const REPOINT_HELP: HelpId = "settings.models.default";

export interface ProviderModalProps {
  provider: string;
  /** The saved document the edit starts from (the org default's effective models). */
  effective: Json | undefined;
  catalog: readonly CatalogModel[];
  /** The switch on the row asked for this state first (turning a provider off that some default needs). */
  initialEnabled?: boolean | undefined;
  saving: boolean;
  /** The last save's refusal, shown in the footer when it names agents, else as a banner. */
  failure: ApiError | null;
  onSave(result: ProviderEditResult): void;
  onClose(): void;
}

const MODES: readonly { value: ProviderPolicy["mode"]; label: string }[] = [
  { value: "follow_aws", label: "Follow AWS" },
  { value: "allow_list", label: "Choose models" },
];

const MODE_SENTENCES: Record<ProviderPolicy["mode"], string> = {
  follow_aws: "Every model of this provider that AWS lets the account call, including ones it adds later.",
  allow_list: "Only the ticked models. A model AWS adds later stays off until it is ticked here.",
};

export function ProviderModal({ provider, effective, catalog, initialEnabled, saving, failure, onSave, onClose }: ProviderModalProps) {
  const { readOnly, reason } = useReadOnly();
  const start = entryFor(globalOf(effective), provider);
  const models = catalog.filter((m) => m.provider === provider);
  const [enabled, setEnabled] = useState(initialEnabled ?? start.enabled);
  const [mode, setMode] = useState(start.mode);
  const [checked, setChecked] = useState<string[]>(() => (start.mode === "allow_list" ? [...start.allowed] : models.map((m) => m.model)));
  const [repoint, setRepoint] = useState<Partial<Record<Harness["key"], string>>>({});
  const label = providerLabel(provider);
  const modeLabel = useId();

  const edit: ProviderEdit = { enabled, mode, allowed: checked, repoint };
  const result = applyProviderEdit({}, effective, provider, edit, catalog);
  // The modal is the Families sub-tab's draft: guarded while its edit differs from the saved entry.
  const saved = applyProviderEdit({}, effective, provider, { enabled: start.enabled, mode: start.mode, allowed: start.allowed }, catalog);
  useUnsavedGuard(JSON.stringify(result.document) !== JSON.stringify(saved.document));
  const refused = failure ? agentErrorsFrom(failure) : {};
  const placed = Object.keys(refused).length > 0;
  const footer = defaultsFooter(effective, result, catalog);
  const toggle = (model: string, on: boolean) => setChecked((current) => (on ? [...current.filter((m) => m !== model), model] : current.filter((m) => m !== model)));

  return (
    <Dialog
      title={`${label} models`}
      open={true}
      onClose={onClose}
      closeLabel="Close"
      class="dialog-wide"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-primary" disabled={readOnly || saving} title={readOnly ? reason ?? "" : undefined} onClick={() => onSave(result)}>
            {saving ? "Saving" : "Save"}
          </button>
        </>
      }
    >
      {/* help-exempt: the dialog's title names what the switch turns on. */}
      <label class="provider-row-switch">
        <input type="checkbox" role="switch" checked={enabled} onChange={(event) => setEnabled(event.currentTarget.checked)} />
        <span>{enabled ? `${label} is on` : `${label} is off: no agent is offered its models`}</span>
      </label>
      {enabled && (
      <div class="stack-sm">
        <span id={modeLabel} class="field-label">
          Which models
        </span>
        <Segmented<ProviderPolicy["mode"]> options={MODES} value={mode} onChange={setMode} labelledBy={modeLabel} small />
        <p class="field-hint">{MODE_SENTENCES[mode]}</p>
        {mode === "allow_list" && (
          <>
            <div class="row">
              <button type="button" class="button-link" onClick={() => setChecked(models.map((m) => m.model))}>
                Select all
              </button>
              <button type="button" class="button-link" onClick={() => setChecked([])}>
                Select none
              </button>
              <span class="field-hint" role="status">
                {models.filter((m) => checked.includes(m.model)).length} of {models.length} allowed
              </span>
            </div>
            {models.length === 0 && <p class="field-hint">No installation has reported a model of this provider yet.</p>}
            <ul class="model-checks" aria-label={`${label} models`}>
              {models.map((m) => (
                <li key={m.model} class={m.invokable ? "model-check" : "model-check model-check-greyed"}>
                  {/* help-exempt: the dialog's title names what the boxes choose. */}
                  <label>
                    <input type="checkbox" checked={checked.includes(m.model)} onChange={(event) => toggle(m.model, event.currentTarget.checked)} />
                    <span class="model-check-name">
                      {m.displayName}
                      <span class="cell-sub mono">{m.model}</span>
                    </span>
                  </label>
                  {!m.invokable && <Chip tone="grey">not invokable in this region</Chip>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      )}
      <ModalFooter lines={footer} result={result} refused={refused} onRepoint={(key, model) => setRepoint((current) => ({ ...current, [key]: model }))} />
      {failure && !placed && <ErrorBanner error={failure} details={true} />}
    </Dialog>
  );
}

function ModalFooter({ lines, result, refused, onRepoint }: { lines: string[]; result: ProviderEditResult; refused: ReturnType<typeof agentErrorsFrom>; onRepoint(key: Harness["key"], model: string): void }) {
  const red = !result.ready || Object.keys(refused).length > 0;
  return (
    <div class={red ? "modal-footer-note modal-footer-red" : "modal-footer-note"} role="status" aria-live="polite">
      <p class="field-hint">Agent defaults after this change:</p>
      <ul>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {result.affectedDefaults.map((hit) => {
        const id = `repoint-${hit.harness.key}`;
        const error = refused[hit.harness.key]?.default;
        return (
          <Field key={hit.harness.key} id={id} label={`Re-point ${hit.harness.label} to`} help={REPOINT_HELP} error={error} hint={hit.options.length === 0 ? `Nothing is left for ${hit.harness.label}; allow a model it can use first.` : undefined}>
            <select id={id} value={hit.repointedTo ?? ""} disabled={hit.options.length === 0} onChange={(event) => onRepoint(hit.harness.key, event.currentTarget.value)}>
              <option value="" disabled>
                Choose a model
              </option>
              {hit.options.map((m) => (
                <option key={m.model} value={m.model}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </Field>
        );
      })}
      {Object.entries(refused)
        .filter(([key]) => !result.affectedDefaults.some((hit) => hit.harness.key === key))
        .map(([key, errors]) => (
          <p key={key} class="field-error" role="alert">
            {errors?.default ?? errors?.narrow}
          </p>
        ))}
    </div>
  );
}

